import { useCallback, useMemo, useState } from 'react';
import { Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { AvailabilityAfterProgramAnswersReviewStep } from './AvailabilityAfterProgramAnswersReviewStep';
import { BasicEligibilityAnswersReviewStep } from './BasicEligibilityAnswersReviewStep';
import { CharacterAndHabitsAnswersReviewStep } from './CharacterAndHabitsAnswersReviewStep';
import { DietaryHabitsAndHealthAnswersReviewStep } from './DietaryHabitsAndHealthAnswersReviewStep';
import { InspirationAndWillingnessAnswersReviewStep } from './InspirationAndWillingnessAnswersReviewStep';
import { PracticeAnswersReviewStep } from './PracticeAnswersReviewStep';
import { SupportAnswersReviewStep } from './SupportAnswersReviewStep';
import { UnderstandingOfHeartfulnessAnswersReviewStep } from './UnderstandingOfHeartfulnessAnswersReviewStep';
import {
	ApplicantAnswersReviewProvider,
	useApplicantAnswersReview,
} from './useApplicantAnswersReview';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import {
	ANSWERS_REVIEW_CONTEXT,
	INTERVIEW_LANDING,
} from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrow from '../../../assets/images/arrow-left.svg';
import useUserSession from '../../../hooks/useUserSession';
import { ALLOWED_ROLES } from '../../../shared/Utils';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const ApplicantAnswersReviewWizard = () => {
	const { onSubmitClick, onCancelClick, application } =
		useApplicantAnswersReview();
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	const currentWizard = 0;
	const stepOffset = 0;

	const wizards = useMemo(
		() => [
			{
				steps: [
					<BasicEligibilityAnswersReviewStep key={1} />,
					<InspirationAndWillingnessAnswersReviewStep key={2} />,
					<PracticeAnswersReviewStep key={3} />,
					<CharacterAndHabitsAnswersReviewStep key={4} />,
					<AvailabilityAfterProgramAnswersReviewStep key={5} />,
					<SupportAnswersReviewStep key={6} />,
					<UnderstandingOfHeartfulnessAnswersReviewStep key={7} />,
					<DietaryHabitsAndHealthAnswersReviewStep key={8} />,
				],
				descriptions: [
					{
						title: t('essentials'),
						description: t('thisIsMandatoryCriteria') ?? '',
					},
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('expectedToBeRoleModel') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	const handleNextClick = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const handlePreviousClick = useCallback(() => {
		if (stepStates[currentWizard] == 0) {
			navigateToPage(ANSWERS_REVIEW_CONTEXT);
		}
	}, [stepStates, navigateToPage]);
	const completedIndex = application?.applicantAnswersReviewed
		? steps.length
		: 0;

	const handleEvaluateClick = useCallback(() => {
		navigateToPage(INTERVIEW_LANDING);
	}, [navigateToPage]);

	return (
		<div
			className={`flex flex-col h-full min-h-0 p-[8px] ${
				!isMobile ? 'w-fit my-0 mx-auto' : 'w-full my-0 mx-auto'
			}`}
		>
			<div className="relative flex flex-col sm:flex-row justify-center items-center w-full">
				<div
					className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer absolute left-0"
					onClick={handleEvaluateClick}
				>
					<Icon src={LeftArrow} width={20} height={20} />
					{!isMobile && (
						<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
							{t('backToEvaluate')}
						</Text>
					)}
				</div>
				<Text className="mt-4 mb-6 font-sans font-bold text-sm text-center text-secondary-black w-full max-w-[250px] sm:max-w-full break-words whitespace-normal">
					{t('candidateApplicationReview')}
				</Text>
			</div>
			<WizardContainer
				onCancelClick={onCancelClick}
				onSubmitClick={
					isRoleNotAllowed ? () => void handleEvaluateClick() : onSubmitClick
				}
				onNextClick={handleNextClick}
				onPreviousClick={handlePreviousClick}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
				draftIndex={completedIndex}
				completedDraftIndex={completedIndex}
			/>
		</div>
	);
};

export const ApplicantAnswersReviewWizardContainer = () => {
	return (
		<ApplicantAnswersReviewProvider>
			<WizardProvider>
				<ApplicantAnswersReviewWizard />
			</WizardProvider>
		</ApplicantAnswersReviewProvider>
	);
};
